import React, { useEffect, useRef } from 'react';
import { useFocusTrap } from '@omq/shared/src/hooks/use-focus-trap';
import { useMergedRef } from '@omq/shared/src/hooks/use-merged-ref';
import { CloseIcon } from './icons';

interface ImageModalProps {
  src: string;
  alt: string;
  onClose: () => void;
  ariaLabel?: string;
  ariaCloseLabel?: string;
  // Localized fallback alt when the source image had none
  altFallback?: string;
}

/**
 * ImageModal component
 * Displays a full-size image in a lightbox overlay within the chat window
 */
export const ImageModal: React.FC<ImageModalProps> = ({
  src,
  alt,
  onClose,
  ariaLabel,
  ariaCloseLabel,
  altFallback,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<Element | null>(null);

  // Shared trap (shadow-root aware): scopes Tab within the overlay, focuses
  // the first tabbable element (the close button) and calls onClose on Escape.
  const focusTrapRef = useFocusTrap(true, onClose);
  const overlayMergedRef = useMergedRef(overlayRef, focusTrapRef);

  // Save previously focused element on mount, restore on unmount. Mount focus
  // is owned by the trap. activeElement must be read from the root node:
  // inside an open shadow root, document.activeElement is the shadow host.
  useEffect(() => {
    const root = overlayRef.current?.getRootNode() as Document | ShadowRoot | undefined;
    previousFocusRef.current = root?.activeElement ?? null;
    return () => {
      if (previousFocusRef.current instanceof HTMLElement) {
        previousFocusRef.current.focus();
      }
    };
  }, []);

  return (
    // Click-to-dismiss backdrop: the keyboard equivalent is Escape (useFocusTrap),
    // so no key handler belongs on the overlay itself — standard modal pattern.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <div
      className="omq-chatbot-image-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel || 'Image preview'}
      ref={overlayMergedRef}
      tabIndex={-1}>
      <button
        className="omq-chatbot-image-modal-close"
        onClick={onClose}
        type="button"
        aria-label={ariaCloseLabel || 'Close image'}>
        <CloseIcon />
      </button>
      {/* stopPropagation shield so clicks on the image don't dismiss the lightbox */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <img
        className="omq-chatbot-image-modal-img"
        src={src}
        alt={alt || altFallback || 'Image from chat'}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};
